import { useMemo } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { IconGeneralText } from 'src/assets/icons/generationTools/IconGeneralText';
import { ButtonText } from 'src/components/ButtonText';
import { ContextMenu } from 'src/components/ContextMenu';
import { CUSTOM_MENU_ITEM_TYPE } from 'src/components/CustomMenuItem';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { GenerationToolIcon } from 'src/components/GenerationToolIcon';
import { useUnlockCustomModelPopup } from 'src/components/UnlockCustomModelPopup';
import {
  formatToolDisplayName,
  GENERATION_TOOL,
  getSectionToolsOptions,
  getToolDefaultAssetType,
  getToolSectionLabel,
  isFreestyleGenerationTool,
  isGeneralTextTool,
  isPluralTool,
} from 'src/data/generationTool';
import { PROJECT_TYPE } from 'src/data/project';
import { determineIndefiniteArticle } from 'src/lib/string';

import { useStyles } from './useStyles';
import { getToolMetricName } from './utils';

export const SectionScoreTooltipContent = ({
  className,
  generationTool,
  projectType,
  supportSize,
  onSelectTool,
  assetType,
}) => {
  const styles = useStyles();

  const isFreestyleTool = isFreestyleGenerationTool(generationTool);

  const resolvedAssetType = assetType || getToolDefaultAssetType(generationTool);
  const toolTitle = formatToolDisplayName(generationTool, { assetType: resolvedAssetType });
  const isPlural = isPluralTool(generationTool);

  const indefiniteArticle = isPlural ? '' : ` ${determineIndefiniteArticle(toolTitle)}`;
  const openUnlockCustomModelPopup = useUnlockCustomModelPopup();
  const handleChangeMetricClick = () => {
    openUnlockCustomModelPopup();
  };

  const metricName = getToolMetricName(generationTool);
  const description =
    !isFreestyleTool && supportSize != null ? (
      <>
        <span>
          <br />
          This score predicts the performance of this text compared to {supportSize} similar texts
          from Anyword’s A-B test dataset. The score predicts {metricName} (
        </span>
        <ButtonText
          className={styles.buttonChangeMetric}
          onClick={handleChangeMetricClick}
        >
          change
        </ButtonText>
        <span>), normalized to be between 0 and 100.</span>
      </>
    ) : (
      ''
    );

  const menuItems = useMemo(() => {
    const toolOptions = getSectionToolsOptions({
      projectType,
      withFreeStyle: true,
    });

    const createClickHandler = (tool) => (event) => {
      event.stopPropagation();
      onSelectTool(tool);
    };

    return toolOptions.map((option) => {
      if (option.tool) {
        return {
          id: option.tool,
          type: CUSTOM_MENU_ITEM_TYPE.default,
          label: getToolSectionLabel(option.tool),
          icon: isFreestyleGenerationTool(option.tool) ? (
            <IconGeneralText className={styles.iconTool} />
          ) : (
            <GenerationToolIcon
              className={styles.iconTool}
              tool={option.tool}
            />
          ),
          onClick: createClickHandler(option.tool),
        };
      } else {
        return {
          id: option.subheader,
          type: CUSTOM_MENU_ITEM_TYPE.subheader,
          label: option.subheader,
        };
      }
    });
  }, [onSelectTool, projectType, styles.iconTool]);
  const changeAssetTypeButtonText = (text) => {
    return (
      <ContextMenu
        classes={{ menuRoot: styles.menuRoot }}
        items={menuItems}
        renderButton={(props) => (
          <ButtonText
            data-testid="scoreTooltipChangeButton"
            className={styles.buttonChangeType}
            {...props}
          >
            {text}
          </ButtonText>
        )}
        menuProps={{
          PopoverClasses: { root: styles.menuWrapper },
        }}
      />
    );
  };

  const title =
    !isFreestyleTool && !isGeneralTextTool(generationTool) ? (
      <>
        We are scoring this as{indefiniteArticle} {toolTitle} ({changeAssetTypeButtonText('Change')}
        ).
      </>
    ) : (
      <>
        General text is not scored for performance.
        <br />
        <br />
        {changeAssetTypeButtonText('Select')} an asset type to score this text.
      </>
    );

  return (
    <div className={cx(styles.root, className)}>
      <div className={styles.header}>
        <CustomTypography
          data-testid="scoreTooltipTitle"
          className={styles.title}
          variant={TYPOGRAPHY_VARIANT.paragraph14}
        >
          {title}
        </CustomTypography>
      </div>

      {description && (
        <CustomTypography
          data-testid="scoreTooltipDescription"
          className={styles.description}
          variant={TYPOGRAPHY_VARIANT.paragraph14}
        >
          {description}
        </CustomTypography>
      )}
    </div>
  );
};

SectionScoreTooltipContent.propTypes = {
  className: PropTypes.string,
  generationTool: PropTypes.oneOf(Object.values(GENERATION_TOOL)).isRequired,
  projectType: PropTypes.oneOf(Object.values(PROJECT_TYPE)),
  supportSize: PropTypes.number,
  onSelectTool: PropTypes.func,
  assetType: PropTypes.string,
};
SectionScoreTooltipContent.defaultProps = {
  className: null,
  projectType: null,
  supportSize: null,
  onSelectTool: null,
  assetType: null,
};
